import { useState, useCallback, useEffect, useRef } from "react";
import { Menu } from "lucide-react";
import ChatInput from "@/components/ChatInput";
import WelcomeHeader from "@/components/WelcomeHeader";
import CollectionGrid from "@/components/CollectionGrid";
import QuickChips from "@/components/QuickChips";
import ChatMessageList from "@/components/ChatMessageList";
import ChatHistorySidebar from "@/components/ChatHistorySidebar";
import { useSmartScroll } from "@/hooks/useScrollToBottom";
import {
  createSession,
  fetchSessionMessages,
  detectCollections,
  fetchCollectionDesigns,
  sendChatMessage,
  sendChatMessageBackground,
  transcribeAudio,
  uploadPaymentScreenshot,
} from "@/services/chatApi";
import { proxyImg } from "@/utils/imageProxy";

const BRAND_LOGO = proxyImg("https://customer-assets.emergentagent.com/job_cake-design-studio-5/artifacts/5xphm2ah_Sugar%20Bouquet%20Logo.png", 64, 80);

const INITIAL_MESSAGE = {
  id: "welcome",
  role: "assistant",
  content: `Welcome to Sugar Bouquet India\n\nI'm here to help you pick the perfect **100% eggless designer buttercream flower** sugar bouquet cake. To get started, please share your **delivery location**, **preferred delivery date**, and **occasion** whenever you're ready.`,
};

export default function ChatPage() {
  const [chatItems, setChatItems] = useState([INITIAL_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showCollections, setShowCollections] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [activeBooking, setActiveBooking] = useState(null);
  const [designConfirmed, setDesignConfirmed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { messagesEndRef, scrollToBottom, scrollToMessage } = useSmartScroll();

  // Refs for values used inside callbacks to avoid stale closures
  const sessionIdRef = useRef(sessionId);
  const isLoadingRef = useRef(isLoading);
  const selectedDesignRef = useRef(selectedDesign);
  const designConfirmedRef = useRef(designConfirmed);
  const selectedCollectionRef = useRef(selectedCollection);
  const activeBookingRef = useRef(activeBooking);

  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
  useEffect(() => { selectedDesignRef.current = selectedDesign; }, [selectedDesign]);
  useEffect(() => { designConfirmedRef.current = designConfirmed; }, [designConfirmed]);
  useEffect(() => { selectedCollectionRef.current = selectedCollection; }, [selectedCollection]);
  useEffect(() => { activeBookingRef.current = activeBooking; }, [activeBooking]);

  useEffect(() => {
    createSession()
      .then(setSessionId)
      .catch(() => setSessionId("local-" + Date.now()));
  }, []);

  const addMessage = useCallback((role, content, extra) => {
    const id = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const msg = { id, role, content, ...extra };
    setChatItems((prev) => [...prev, msg]);
    return id;
  }, []);

  const addGalleryItem = useCallback((collectionName, designs) => {
    setChatItems((prev) => [...prev, { id: `gallery-${Date.now()}`, type: "gallery", collectionName, designs }]);
  }, []);

  const addPaymentItem = useCallback((bookingId, totalAmount, customerName, customerPhone, customerEmail) => {
    setChatItems((prev) => [...prev, { id: `payment-${Date.now()}`, type: "payment", bookingId, totalAmount, customerName, customerPhone, customerEmail }]);
    setActiveBooking({ bookingId, totalAmount, customerName, customerPhone, customerEmail });
  }, []);

  // Refs are used intentionally inside callbacks to avoid stale closures and unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchDesignsForMessage = useCallback(async (text) => {
    if (selectedDesignRef.current || designConfirmedRef.current) return null;
    const hasPhoneNumber = /\d{10}/.test(text);
    const hasMultipleDetails = text.length > 80;
    const isExplicitDesignRequest = /\b(show|browse|see|view|explore|display)\b.*\b(design|collection|menu|cake|option)/i.test(text)
      || (/\b(design|collection|menu)\b/i.test(text) && text.split(/\s+/).length < 8);
    if ((hasPhoneNumber || hasMultipleDetails) && !isExplicitDesignRequest) return null;

    try {
      const data = await detectCollections(text, !!selectedDesignRef.current);
      if (data.show_menu) return { showMenu: true };
      const matched = data.matched_collections;
      if (matched?.length > 0) {
        for (const match of matched) {
          if (match.has_designs) {
            const designs = await fetchCollectionDesigns(match.collection_id);
            if (designs.length > 0) {
              return { collectionName: match.collection_name, collectionId: match.collection_id, designs };
            }
          }
        }
      }
    } catch (err) {
      console.error("Collection detection failed:", err);
    }
    return null;
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoadingRef.current) return;
    addMessage("user", text);
    setIsLoading(true);
    setShowCollections(false);
    scrollToBottom();

    // Run detect-collection and chat API in PARALLEL for speed
    const designPromise = fetchDesignsForMessage(text);
    const chatPromise = sendChatMessage(sessionIdRef.current, text);

    const designData = await designPromise;

    if (designData?.showMenu) {
      const aid = addMessage("assistant", "We have 14 stunning designer collections for you! Click the **\"Click to see all designs\"** button below any of my messages to explore all our collections with images and pricing!\n\n**Today Only \u2014 5% OFF** on all orders!");
      scrollToMessage(aid);
      setShowCollections(true);
      setIsLoading(false);
      // Chat was already fired in parallel — just let it complete in background
      chatPromise.catch(() => {});
      return;
    }

    if (designData?.designs) {
      const aid = addMessage("assistant", "Here are our stunning designs! Tap any design you love \u2014 you can always browse more or change your choice! **Today Only \u2014 5% OFF** on all orders!");
      scrollToMessage(aid);
      addGalleryItem(designData.collectionName, designData.designs);
      setIsLoading(false);
      // Chat was already fired in parallel — let it complete
      chatPromise.catch(() => {});
    } else {
      try {
        const res = await chatPromise;
        const aid = addMessage("assistant", res.response);
        scrollToMessage(aid);
        if (res.booking_data) {
          const bd = res.booking_data;
          addPaymentItem(bd.booking_id, bd.total_amount, bd.customer_name, bd.phone, bd.email);
        }
      } catch (err) {
        console.error("Chat failed:", err);
        addMessage("assistant", "I'm sorry, I'm having a little trouble right now. Could you try again?");
      }
      setIsLoading(false);
    }
  }, [addMessage, addGalleryItem, addPaymentItem, fetchDesignsForMessage, scrollToMessage, scrollToBottom]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleImageUpload = useCallback(async (file, messageText) => {
    const booking = activeBookingRef.current;
    if (!booking) {
      addMessage("user", messageText || "Uploaded an image", { hasImage: true, imagePreview: URL.createObjectURL(file) });
      const aid = addMessage("assistant", "I can see you've uploaded an image! If this is a payment screenshot, please first complete the booking process so I can verify it against your order total. If you need help, just ask!");
      scrollToMessage(aid);
      return;
    }
    addMessage("user", messageText || "Payment screenshot uploaded", { hasImage: true, imagePreview: URL.createObjectURL(file) });
    setIsLoading(true);
    scrollToBottom();
    try {
      const data = await uploadPaymentScreenshot(booking.bookingId, file);
      let aid;
      if (data.fully_paid) {
        const countNote = data.payments_count > 1 ? ` (verified across ${data.payments_count} payments)` : "";
        aid = addMessage("assistant", `**Payment Verified!** Your payment of Rs ${data.expected_amount} has been confirmed${countNote}.\n\nYour order **${booking.bookingId}** is now confirmed!\n\nYou can download your order summary PDF below. We'll start preparing your beautiful buttercream flower cupcake bouquet!\n\nThank you for choosing Sugar Bouquet India!`);
      } else if (data.status === "partial") {
        aid = addMessage("assistant", `**Payment Recorded!** Rs ${data.cumulative_verified} of Rs ${data.expected_amount} verified so far.\n\n**Remaining: Rs ${data.remaining_amount}**\n\nPlease make the next payment and upload the screenshot to complete verification.`);
      } else if (data.status === "mismatch") {
        const found = data.scan_result?.amount_found;
        aid = addMessage("assistant", `I couldn't detect a valid payment amount from this screenshot${found ? ` (found Rs ${found})` : ""}.\n\nPlease ensure the correct amount was paid and upload a clearer screenshot.`);
      } else {
        aid = addMessage("assistant", "I couldn't read the payment screenshot clearly. Please try uploading a clearer image or contact us on WhatsApp: https://wa.me/c/918884915270");
      }
      scrollToMessage(aid);
    } catch (err) {
      console.error("Payment screenshot scan failed:", err);
      const aid = addMessage("assistant", "Something went wrong while scanning. Please try again or contact us on WhatsApp: https://wa.me/c/918884915270");
      scrollToMessage(aid);
    }
    setIsLoading(false);
  }, [addMessage, scrollToMessage, scrollToBottom]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCollectionClick = useCallback(async (collection) => {
    setSelectedCollection(collection);
    addMessage("user", `Show me ${collection.name} designs ${collection.emoji}`);
    setShowCollections(false);
    setIsLoading(true);
    scrollToBottom();
    try {
      const designs = await fetchCollectionDesigns(collection.id);
      if (designs.length > 0) {
        const aid = addMessage("assistant", `Here are our stunning ${collection.name} designs! ${collection.emoji} Each one is handcrafted with 100% edible buttercream flowers. Tap any design you love!\n\n**Today Only \u2014 5% OFF** on all orders!`);
        scrollToMessage(aid);
        addGalleryItem(collection.name, designs);
        setIsLoading(false);
        sendChatMessageBackground(sessionIdRef.current, `Show me ${collection.name} designs`);
      } else {
        const aid = addMessage("assistant", `Our ${collection.name} collection ${collection.emoji} features beautiful designer buttercream flower cupcake bouquets! We're adding design images soon. Meanwhile, choose any design from our Most Popular collection and get it customized for your occasion!\n\n**Today Only \u2014 5% OFF** on all orders!`);
        scrollToMessage(aid);
        setIsLoading(false);
        sendChatMessageBackground(sessionIdRef.current, `Tell me about ${collection.name} collection`);
      }
    } catch (err) {
      console.error("Failed to load collection designs:", err);
      addMessage("assistant", `Let me show you our ${collection.name} collection. Please try again!`);
      setIsLoading(false);
    }
  }, [addMessage, addGalleryItem, scrollToMessage, scrollToBottom]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDesignSelect = useCallback((design) => {
    const isReselection = designConfirmedRef.current;
    setSelectedDesign(design);
    setDesignConfirmed(true);

    let aid;
    if (isReselection) {
      aid = addMessage("assistant",
        `Design updated to **"${design.name}"**!\n\n${design.description}\n\nAll your previously provided details are still saved. Let me check what's remaining...`,
        { selectedDesignCard: { name: design.name, image: design.image, description: design.description } }
      );
      sendChatMessageBackground(sessionIdRef.current, `I changed my design to "${design.name}". Please update only the design in my order. Keep ALL other details I already provided (city, date, slot, flavour, size, occasion, name, phone, address) exactly as they were. Show me a summary with green checkmarks for everything already collected, and only ask for what's still missing.`);
    } else {
      const collection = selectedCollectionRef.current;
      const occasionText = collection ? ` for your **${collection.name}** occasion` : "";
      aid = addMessage("assistant",
        `Great choice! You selected **\u201C${design.name}\u201D**${occasionText}\n\n${design.description}\n\nTo complete your order, please share the remaining details:\n- **Delivery city** (Mumbai, Thane, Navi Mumbai, or Pune)\n- **Delivery date** (minimum next day)\n- **Preferred size** (${design.available_sizes?.join(", ") || "8, 12, or 16 servings"})\n- **Flavour** (Mississippi Mud Chocolate, Dreamy Vanilla, or Red Velvet)\n- **Delivery slot** (12 PM-3 PM or 3 PM-6 PM)\n\nFeel free to share all at once or one at a time!`,
        { selectedDesignCard: { name: design.name, image: design.image, description: design.description } }
      );
      const collectionContext = collection ? `, occasion: ${collection.name}` : "";
      sendChatMessageBackground(sessionIdRef.current, `I selected the "${design.name}" design${collectionContext}. Please remember this selection and do not ask me about design or occasion again.`);
    }
    scrollToMessage(aid);
  }, [addMessage, scrollToMessage]);

  const handleBrowseAll = useCallback(() => {
    setShowCollections(true);
    setTimeout(() => scrollToBottom(), 200);
  }, [scrollToBottom]);

  const handleTranscription = useCallback(async (audioBlob) => {
    try {
      return await transcribeAudio(audioBlob);
    } catch (err) {
      return null;
    }
  }, []);

  const handlePaymentVerified = useCallback(() => {
    setActiveBooking((prev) => prev ? { ...prev, verified: true } : null);
  }, []);

  const handleNewChat = useCallback(async () => {
    try {
      const sid = await createSession();
      setSessionId(sid);
    } catch (err) {
      setSessionId("local-" + Date.now());
    }
    setChatItems([INITIAL_MESSAGE]);
    setShowCollections(false);
    setSelectedDesign(null);
    setSelectedCollection(null);
    setActiveBooking(null);
    setDesignConfirmed(false);
  }, []);

  const handleSessionSelect = useCallback(async (sid) => {
    setSessionId(sid);
    setShowCollections(false);
    setSelectedDesign(null);
    setSelectedCollection(null);
    setActiveBooking(null);
    setDesignConfirmed(false);
    try {
      const msgs = await fetchSessionMessages(sid);
      setChatItems(msgs.length > 0 ? msgs : [INITIAL_MESSAGE]);
    } catch (err) {
      setChatItems([INITIAL_MESSAGE]);
    }
  }, []);

  const hasUserMessages = chatItems.some((item) => item.role === "user");

  return (
    <div className="flex flex-col h-screen bg-white" data-testid="chat-page">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 bg-white/80 backdrop-blur-xl" data-testid="top-bar">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          data-testid="open-sidebar-btn"
        >
          <Menu className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex items-center gap-2">
          <img src={BRAND_LOGO} alt="Sugar Bouquet" className="w-7 h-7 object-contain" />
          <span className="text-sm font-semibold text-gray-800 tracking-tight">Sugar Bouquet India</span>
        </div>
      </div>

      <ChatHistorySidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentSessionId={sessionId}
        onSessionSelect={handleSessionSelect}
        onNewChat={handleNewChat}
      />

      <div className="flex-1 overflow-y-auto" data-testid="chat-messages-area">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-44">
          {!hasUserMessages && <WelcomeHeader />}
          {!hasUserMessages && <QuickChips onChipClick={sendMessage} onShowCollections={() => setShowCollections(true)} />}

          <ChatMessageList
            chatItems={chatItems}
            selectedDesign={selectedDesign}
            onDesignSelect={handleDesignSelect}
            onPaymentVerified={handlePaymentVerified}
            onBrowseAll={handleBrowseAll}
          />

          {showCollections && (
            <div className="mt-6 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
              <CollectionGrid onCollectionClick={handleCollectionClick} />
            </div>
          )}

          {isLoading && (
            <div className="flex gap-3 mt-6 animate-fade-in" data-testid="typing-indicator">
              <div className="h-8 w-8 rounded-full overflow-hidden bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                <img src={BRAND_LOGO} alt="SB" className="w-full h-full object-contain p-0.5" />
              </div>
              <div className="bg-gray-50 rounded-2xl px-5 py-4 flex gap-1.5 items-center">
                <span className="typing-dot w-2 h-2 bg-gray-400 rounded-full inline-block" />
                <span className="typing-dot w-2 h-2 bg-gray-400 rounded-full inline-block" />
                <span className="typing-dot w-2 h-2 bg-gray-400 rounded-full inline-block" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      <ChatInput
        onSend={sendMessage}
        onTranscribe={handleTranscription}
        onImageUpload={handleImageUpload}
        isLoading={isLoading}
      />
    </div>
  );
}
